"use client";

import React, { useEffect } from "react";
import { Formik, Form } from "formik";
import {
  insertSetting,
  updateSetting,
  findAllSetting,
  findByIDSetting,
} from "./setting.service";
import { settingValidationSchema } from "./setting.validation";
import { clearSettingDetails } from "./setting.slice";
import { closed } from "@/redux/slices/drawerSlice";
import { STATUS_OPTIONS } from "@/lib/constants";
import { InputField } from "@/components/common/FormFields/InputField";
import { SelectField } from "@/components/common/FormFields/SelectField";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { CustomButton } from "@/components/common/CustomButton";
import { Save, X } from "lucide-react";
import { FormLoader } from "@/components/common/FormLoader";

const SettingForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const { SettingDetail, formLoading } = useAppSelector(
    (state) => state.setting,
  );
  const { data: SettingID } = useAppSelector((state) => state.drawer);

  useEffect(() => {
    if (SettingID) {
      dispatch(findByIDSetting(SettingID));
    }
  }, [SettingID, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearSettingDetails());
    };
  }, [dispatch]);

  const handleSubmit = async (values: any) => {
    const payload = {
      ...values,
      ID: SettingID || 0,
      IsActive: String(values.IsActive) === "true",
    };

    if (SettingID) {
      await dispatch(updateSetting(payload));
    } else {
      await dispatch(insertSetting(payload));
    }

    dispatch(findAllSetting());
    dispatch(closed());
  };

  return (
    <div className="relative">
      <FormLoader loading={formLoading} />
      <Formik
        initialValues={{
          SettingKey: SettingDetail.SettingKey || "",
          SettingValue: SettingDetail.SettingValue || "",
          SettingType: SettingDetail.SettingType || "",
          IsActive: SettingDetail.IsActive ?? true,
        }}
        validationSchema={settingValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {() => (
          <Form className="flex flex-col gap-6">
            <div className="space-y-4">
              <InputField
                label="Setting Key"
                name="SettingKey"
                placeholder="Enter setting key"
                required
                disabled={!!SettingID}
              />

              <InputField
                label="Setting Value"
                name="SettingValue"
                placeholder="Enter setting value"
              />

              <InputField
                label="Setting Type"
                name="SettingType"
                placeholder="Enter setting type"
              />

              <SelectField
                label="Status"
                name="IsActive"
                options={STATUS_OPTIONS}
                required
              />
            </div>

            <div className="flex items-center gap-3 pt-6 mt-6 border-t border-slate-100">
              <CustomButton
                type="submit"
                variant="primary"
                loading={formLoading}
                icon={<Save size={14} />}
                fullWidth
              >
                {SettingID ? "Update" : "Insert"}
              </CustomButton>
              <CustomButton
                type="button"
                variant="outline"
                icon={<X size={14} />}
                onClick={() => dispatch(closed())}
                disabled={formLoading}
                fullWidth
              >
                Cancel
              </CustomButton>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default SettingForm;
